import React, { useEffect, useState } from 'react';
import { GraduationCap, ChevronDown } from 'lucide-react';
import { MCQ } from './types/mcq';
import { CurrentMCQ } from './components/CurrentMCQ';
import { Settings } from './components/Settings';
import { ApiDebugPanel } from './components/ApiDebugPanel';
import { fetchMCQsBySubtopic, markAsDuplicate, markAsCompared, updateMCQ, initializeBaserow } from './services/baserow/api';
import { findSemanticSimilarity } from './services/openai';
import { getApiConfig, updateApiConfig } from './services/api-config';
import { Button } from './components/ui/Button';
import { useSubtopics } from './hooks/useSubtopics';
import { QuestionCounter } from './components/QuestionCounter';

export default function App() {
  const [selectedSubtopic, setSelectedSubtopic] = useState<number | null>(null);
  const [isSubtopicOpen, setIsSubtopicOpen] = useState(false);
  const [currentMCQ, setCurrentMCQ] = useState<MCQ | null>(null);
  const [mcqs, setMcqs] = useState<MCQ[]>([]);
  const [potentialMatches, setPotentialMatches] = useState<MCQ[]>([]);
  const [loading, setLoading] = useState(false);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiConfig, setApiConfig] = useState(getApiConfig());
  const [totalCompared, setTotalCompared] = useState(0);
  const { subtopics, loading: subtopicsLoading } = useSubtopics();
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    initializeBaserow().catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedSubtopic) {
      loadMCQs(selectedSubtopic);
      setIsSubtopicOpen(false);
    }
  }, [selectedSubtopic]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById('subtopic-dropdown');
      const button = document.getElementById('subtopic-button');
      if (
        dropdown && 
        button && 
        !dropdown.contains(event.target as Node) && 
        !button.contains(event.target as Node)
      ) {
        setIsSubtopicOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMCQs = async (subtopicId: number) => {
    setLoading(true);
    setError(null);
    try {
      const fetchedMCQs = await fetchMCQsBySubtopic(String(subtopicId));
      if (fetchedMCQs.length === 0) {
        setError("No MCQs found for this subtopic.");
      } else {
        setMcqs(fetchedMCQs);
        setCurrentMCQ(fetchedMCQs[0]);
        setPotentialMatches([]);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load MCQs';
      console.error("Error loading MCQs:", message);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleFindMatches = async () => {
    if (!currentMCQ || !mcqs.length) return;
    
    setMatchesLoading(true);
    setError(null);
    try {
      const compareMCQs = mcqs.filter(mcq => 
        mcq.id !== currentMCQ.id && !mcq.isDuplicate
      );

      setTotalCompared(compareMCQs.length);

      const similarities = await findSemanticSimilarity(
        currentMCQ.fullMcq,
        compareMCQs.map(mcq => mcq.fullMcq)
      );

      const matchesWithScores = compareMCQs.map((mcq, index) => ({
        ...mcq,
        similarityScore: similarities[index]
      }));

      const significantMatches = matchesWithScores.filter(mcq => 
        (mcq.similarityScore || 0) > 0.7
      );

      setPotentialMatches(significantMatches);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to find matches';
      console.error("Error finding matches:", message);
      setError(message);
    } finally {
      setMatchesLoading(false);
    }
  };

  const handleNextQuestion = async () => {
    if (!mcqs.length || !currentMCQ) return;
    
    setError(null);
    try {
      await markAsCompared(currentMCQ.id);
      
      const currentIndex = mcqs.findIndex(mcq => mcq.id === currentMCQ.id);
      const nextIndex = (currentIndex + 1) % mcqs.length;
      
      setMcqs(prev => prev.filter(mcq => mcq.id !== currentMCQ.id));
      
      const nextMCQ = mcqs[nextIndex];
      setCurrentMCQ(nextMCQ);
      
      setPotentialMatches([]);
      setRefreshCounter(prev => prev + 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to move to next question';
      console.error("Error updating MCQ:", message);
      setError(message);
    }
  };

  const handleConfirmDuplicate = async (duplicateId: number) => {
    if (!currentMCQ) return;
    
    setError(null);
    try {
      await Promise.all([
        markAsDuplicate(duplicateId),
        markAsCompared(duplicateId)
      ]);
      
      setPotentialMatches(prev => prev.filter(mcq => mcq.id !== duplicateId));
      setMcqs(prev => prev.filter(mcq => mcq.id !== duplicateId));
      setRefreshCounter(prev => prev + 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to mark as duplicate';
      console.error("Error updating MCQ:", message);
      setError(message);
    }
  };

  const handleMarkCurrentAsDuplicate = async () => {
    if (!currentMCQ) return;
    
    setError(null);
    try {
      await Promise.all([
        markAsDuplicate(currentMCQ.id),
        markAsCompared(currentMCQ.id)
      ]);
      
      handleNextQuestion();
      setRefreshCounter(prev => prev + 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to mark current question as duplicate';
      console.error("Error marking current MCQ as duplicate:", message);
      setError(message);
    }
  };

  const handleUpdateMCQ = async (updatedMCQ: MCQ) => {
    setError(null);
    try {
      const updated = await updateMCQ(updatedMCQ.id, updatedMCQ);
      
      if (currentMCQ?.id === updated.id) {
        setCurrentMCQ(updated);
      }
      
      setMcqs(prev => prev.map(mcq => 
        mcq.id === updated.id ? updated : mcq
      ));
      
      setPotentialMatches(prev => prev.map(mcq => 
        mcq.id === updated.id ? updated : mcq
      ));
      
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update MCQ';
      console.error("Error updating MCQ:", message);
      setError(message);
    }
  };

  const handleUpdateApiSettings = (settings: { provider: 'openai' | 'groq'; apiKey: string }) => {
    updateApiConfig(settings);
    setApiConfig(settings);
  };

  const getSelectedSubtopicName = () => {
    if (!selectedSubtopic) return '';
    return subtopics.find(s => s.id === selectedSubtopic)?.value || '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-4 sm:py-6 lg:py-8">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-blue-600 bg-opacity-10 p-2 sm:p-3 rounded-xl">
                  <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800">
                    MCQ Duplicate Checker
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Find and mark duplicate questions
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col w-full sm:w-auto gap-2">
                <div className="relative flex-1 sm:flex-none">
                  <Button
                    id="subtopic-button"
                    onClick={() => setIsSubtopicOpen(!isSubtopicOpen)}
                    variant="outline"
                    className="w-full sm:w-auto text-blue-600 hover:text-blue-700 border-blue-200 hover:bg-blue-50 shadow-sm transition-all duration-200 min-w-[160px] justify-between"
                  >
                    <span className="font-medium truncate">
                      {selectedSubtopic ? getSelectedSubtopicName() : 'Select Subtopic'}
                    </span>
                    <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${
                      isSubtopicOpen ? 'transform rotate-180' : ''
                    }`} />
                  </Button>

                  {isSubtopicOpen && (
                    <div
                      id="subtopic-dropdown"
                      className="absolute right-0 mt-2 w-full sm:w-64 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50"
                    >
                      <div className="max-h-64 overflow-y-auto">
                        {subtopicsLoading ? (
                          <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
                        ) : (
                          subtopics.map((subtopic) => (
                            <button
                              key={subtopic.id}
                              onClick={() => setSelectedSubtopic(subtopic.id)}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition-colors ${
                                selectedSubtopic === subtopic.id
                                  ? 'bg-blue-50 text-blue-700 font-medium'
                                  : 'text-gray-700'
                              }`}
                            >
                              {subtopic.value}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="w-full sm:w-auto flex justify-end">
                  <QuestionCounter 
                    subtopicId={selectedSubtopic} 
                    refreshTrigger={refreshCounter}
                  />
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {currentMCQ && (
            <CurrentMCQ
              mcq={currentMCQ}
              potentialMatches={potentialMatches}
              onFindMatches={handleFindMatches}
              onNextQuestion={handleNextQuestion}
              onConfirmDuplicate={handleConfirmDuplicate}
              onMarkCurrentAsDuplicate={handleMarkCurrentAsDuplicate}
              onUpdateMCQ={handleUpdateMCQ}
              loading={loading}
              matchesLoading={matchesLoading}
            />
          )}
        </div>
      </div>

      <Settings
        onSave={handleUpdateApiSettings}
        currentProvider={apiConfig.provider}
        currentApiKey={apiConfig.apiKey}
      />
      
      <ApiDebugPanel />
    </div>
  );
}